(ns ui.mcp.guide
  "The content of the Storrito MCP page: the organization's MCP URL, the
   setup steps with their mockups and hints, and the documentation
   around them. Pure data and functions, so `ui.mcp.page` stays a thin
   layout and the content can be tested."
  (:require [ui.mcp.mockups :as mockups]))

(defn mcp-url
  "The MCP server URL of the organization served at `origin`, for
   example `https://ORG_UUID.storrito.com`."
  [origin]
  (str origin "/mcp"))

(defn claude-code-command
  "The command that registers the MCP server in Claude Code, which sends
   an API credential as Bearer token instead of signing in."
  [mcp-url]
  (str "claude mcp add --transport http storrito " mcp-url
       " --header \"Authorization: Bearer <credential-id>:<credential-secret>\""))

(def intro
  "Let Claude schedule your posts. The Storrito MCP server connects AI assistants to your Storrito organization through the Model Context Protocol (MCP). Once connected, you can ask Claude in plain language to schedule Instagram stories, Instagram reels and TikTok posts, check their status or cancel them, and Claude does it with the same procedures as the Storrito API.")

(def requirements
  ["A Claude account on a plan with custom connectors. At the time of writing these are the paid plans Pro, Max, Team and Enterprise."
   "A Storrito login for this organization. Every team member connects Claude with their own login, Claude then acts with the permissions of that user."
   "A browser for the setup on claude.ai. The connector is stored in your Claude account, so the Claude desktop and mobile apps can use it afterwards as well."])

(defn steps
  "The setup steps in order. Each step has a `:title`, an optional
   `:description`, the `:mockup` of the Claude screen and the `:hints`
   for the numbered markers on the mockup. `url-field` is the real,
   copyable URL control the page renders into the connector dialog."
  [{:keys [mcp-url url-field]}]
  [{:title "Open the connector settings in Claude"
    :description "In Claude.ai open the settings via your name in the bottom left corner. Connectors are listed in the Customize section of the settings."
    :mockup (mockups/settings-connectors {})
    :hints ["Choose Connectors in the Customize section."
            "Click Add in the top right corner and choose Add custom connector."]}
   {:title "Enter the Storrito MCP server"
    :description "Claude asks for a name and the address of the MCP server. Nothing else is needed: no API key, no client ID and no secret."
    :mockup (mockups/add-custom-connector {:mcp-url mcp-url
                                           :url-field url-field})
    :hints ["Name: enter Storrito. The name only labels the connector in your list."
            [:span "Remote MCP server URL: the URL of your organization, "
             [:code {:class ["rounded" "bg-gray-100" "px-1" "text-gray-800"]} mcp-url]
             ". Copy it with the Copy button right in the field."]
            "Click Continue."]}
   {:title "Sign in with your Storrito account"
    :description "Claude opens a Storrito sign-in window. This is how Claude gets its access: it never sees your password, it receives a token that you can revoke at any time by removing the connector."
    :mockup (mockups/sign-in {})
    :hints ["Sign in with the login you use for Storrito. The user has to belong to this organization, the MCP URL belongs to exactly one organization."
            "Confirm with Allow. The window closes and you are back in the Claude settings."]}
   {:title "Check that Storrito is connected"
    :mockup (mockups/connector-connected {:mcp-url mcp-url})
    :hints ["A check mark in the Status column means the connection works. If Claude shows Reconnect instead, click it and sign in again."]}
   {:title "Enable Storrito in a chat"
    :description "Connectors are switched on per chat. Open a new chat and check the tools menu of the message field."
    :mockup (mockups/chat-tools-menu {})
    :hints ["Make sure the Storrito switch is on."
            "Describe in plain language what you want to post. Mention Storrito if Claude does not use it on its own."]}
   {:title "Allow Claude to use the Storrito tools"
    :description "Before Claude changes anything in Storrito, it asks for your permission."
    :mockup (mockups/tool-permission {})
    :hints ["Allow once confirms this single action. Allow always confirms all further uses of this tool in the chat."]}])

(def upload-host
  "The host of the upload URLs that `create-temp-blob-upload-url`
   returns (see `storrito.temp-blob.store`). Claude uploads a file from
   the chat to it."
  "storrito.t3.storage.dev")

(def upload-intro
  "Claude needs the image or the video of a post. A link to media with a public URL always works. A file that you attach to the chat is uploaded by Claude from its own sandbox, which Claude blocks until you allow the storage of Storrito in its settings. You do this once.")

(defn upload-setting
  "The setting in Claude that allows the upload of a file from the chat,
   in the shape of a step of `steps`. `host-field` is the real, copyable
   control for the domain that the page renders into the settings."
  [{:keys [host-field]}]
  {:description "Without the setting Claude reports \"Host not in allowlist\" and can only send a strongly reduced image, and no video."
   :mockup (mockups/settings-capabilities {:host upload-host
                                           :host-field host-field})
   :hints ["In Claude.ai open the settings via your name in the bottom left corner and choose Capabilities."
           "Turn on Cloud code execution and file creation."
           "Turn on Allow network egress. The Domain allowlist can stay on Package managers only."
           [:span "Enter "
            [:code {:class ["rounded" "bg-gray-100" "px-1" "text-gray-800"]} upload-host]
            " under Additional allowed domains and click Add. On a Claude Team or Enterprise plan an Owner does this in the organization settings."]]})

(def example-prompts
  ["Which Instagram accounts are connected to Storrito?"
   "Schedule this photo as an Instagram story for tomorrow at 9:00 and add a link sticker to example.com."
   "Schedule this video as an Instagram reel for Thursday at 12:00 with the caption \"New menu\"."
   "Post this video on TikTok on Friday at 18:00 with the caption \"Behind the scenes\"."
   "What is the status of the story you scheduled this morning?"
   "Cancel the Instagram story that is scheduled for Saturday."])

(def capabilities
  [{:title "Instagram stories"
    :description "Schedule stories from images or videos, with link, poll, question and other stickers. Check their status and cancel them."}
   {:title "Instagram reels"
    :description "Schedule reels from videos with a caption. Check their status and cancel them."}
   {:title "TikTok posts"
    :description "List the connected TikTok accounts, schedule posts, check their status and cancel them."}
   {:title "Media"
    :description "Use an image or a video from a link, or from a file you attach to the chat. For an attached file Claude has to be allowed to upload it, see the section about files from the chat."}
   {:title "Accounts"
    :description "List the connected Instagram accounts and create a link to connect a new one."}])

(def troubleshooting
  [{:problem "No sign-in window opens after Continue."
    :solution "Your browser blocked the popup. Allow popups for claude.ai, then click Connect next to Storrito in the connectors list."}
   {:problem "The sign-in ends with an error about the organization."
    :solution "You signed in with a user of another Storrito organization. Use the URL shown on this page together with the login of a user of this organization."}
   {:problem "Storrito shows Reconnect in the connectors list."
    :solution "The session expired. Click Reconnect and sign in again."}
   {:problem "Claude answers without using Storrito."
    :solution "Check that the Storrito switch in the tools menu of the chat is on, and mention Storrito in your message."}
   {:problem "Claude reports \"Host not in allowlist: storrito.t3.storage.dev\" for a file you attached."
    :solution "Claude blocks uploads from its sandbox by default. Allow the storage of Storrito in the settings of Claude, see the section about files from the chat. A link to the image or video works in any case."}
   {:problem "Claude says it could not use a tool."
    :solution "Claude asks for permission before it uses a Storrito tool. Answer with Allow once or Allow always."}])

(def other-clients
  "Claude Code, Cursor and other MCP clients that do not open a sign-in window authenticate with an API credential instead. Create one under Manage API Credentials and register the server with the credential as Bearer token. For Claude Code the command is:")

(def remove-access
  [{:title "In Claude"
    :description "Open Settings, Connectors, and remove Storrito. This deletes the token Claude uses for your organization. Every team member who connected Claude does this for their own account."}
   {:title "For API credentials"
    :description "Delete the credential under Manage API Credentials. Clients that use it lose access immediately."}])
